// src/pages/StorefrontReport.jsx
// STATUS: active | UPDATED: 2026-08-29
// LOG:
//   2026-08-29  New. The public Janda Digital Storefront Report.
//
// What a dealer principal opens when Jazz sends them a link.
//
// PUBLIC ON PURPOSE, AND THE ONLY PAGE IN THIS APP THAT IS
//
// The reader has no account and never will. The route sits outside
// ProtectedRoute and reads through netlify/functions/storefront-view.js, which
// resolves one row by exact token using the service role. There is no public RLS
// policy on storefront_reports, so a leaked token exposes one report and can
// never expose the table.
//
// THIS IS JANDA DIGITAL, NOT THE ACADEMY
//
// Different division, different audience. No sidebar, no app chrome, no J13
// curriculum language. It reads as a consultancy document because that is what it
// is, and because the person reading it has not bought anything yet.
//
// ONE ACCENT, ONE MEANING
//
// Oxblood appears on exactly one score bar, the lowest one, and on the eyebrow.
// Everything else is ink on paper. Traffic light colours would make this look
// like a free SEO widget rather than something worth paying for.

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Flex, HStack, VStack, Text, Spinner, Divider } from '@chakra-ui/react'
import DealershipLogo from '../components/ui/DealershipLogo'

const CATEGORY_COPY = {
  found: ['Found', 'Whether search engines understand what this business is and where it sits.'],
  fast: ['Fast', 'How quickly the site appears, especially on a phone on rural data.'],
  trusted: ['Trusted', 'Security, email authentication and whether the site works for everyone.'],
  shareable: ['Shareable', 'What a link to this site looks like when somebody posts it.'],
  converting: ['Converting', 'Whether a visit can become a phone call or a lead.'],
}

function ScoreRow({ name, blurb, value, worst }) {
  return (
    <Box borderBottom="1px solid" borderColor="line" py={4}>
      <Flex align="center" gap={4}>
        <Text
          minW={{ base: '92px', md: '124px' }}
          fontSize={{ base: 'lg', md: 'xl' }}
          fontWeight={600}
          color="ink"
          textTransform="uppercase"
          letterSpacing="0.03em"
        >
          {name}
        </Text>
        <Box flex={1} h="9px" bg="surface" borderRadius="1px" overflow="hidden">
          <Box
            h="100%"
            w={`${value ?? 0}%`}
            bg={worst ? 'accent.500' : 'ink'}
            borderRadius="1px"
            transition="width 0.6s cubic-bezier(0.2, 0, 0, 1)"
          />
        </Box>
        <Text minW="44px" textAlign="right" fontSize="2xl" fontWeight={600} color="ink" fontVariantNumeric="tabular-nums">
          {value ?? '--'}
        </Text>
      </Flex>
      <Text fontSize="sm" color="inkMuted" mt={1} lineHeight={1.5}>
        {blurb}
      </Text>
    </Box>
  )
}

function Finding({ f }) {
  const high = f.severity === 'high'
  return (
    <Box borderBottom="1px solid" borderColor="line" py={6}>
      <HStack spacing={2} mb={2}>
        <Box w="7px" h="7px" borderRadius="full" bg={high ? 'accent.500' : 'inkMuted'} />
        <Text
          fontFamily="mono" fontSize="10px" letterSpacing="0.11em"
          textTransform="uppercase" color={high ? 'accent.500' : 'inkMuted'} fontWeight={500}
        >
          {f.category}
        </Text>
      </HStack>
      <Text fontSize="xl" fontWeight={600} color="ink" mb={2} lineHeight={1.2}>
        {f.title}
      </Text>
      <Text fontSize="md" color="inkMuted" lineHeight={1.65} maxW="measure">
        {f.detail}
      </Text>
    </Box>
  )
}


// Where this store sits against everything Janda Digital has measured.
//
// Never names another dealership. This document goes to a dealer principal and
// naming a competitor's score is a different document with different
// consequences. Cohorts and ranges only, and it renders nothing at all when the
// sample is too small to mean anything.
function HowThisCompares({ peers, overall, platform }) {
  if (!peers) return null

  const gap = peers.best - overall
  const onTemplate = platform && !platform.custom

  return (
    <Box mt={14}>
      <Text fontFamily="mono" fontSize="11px" letterSpacing="0.13em" textTransform="uppercase" color="inkMuted" mb={3}>
        Measured the same way
      </Text>
      <Text fontSize={{ base: '26px', md: '32px' }} fontWeight={600} color="ink" textTransform="uppercase" letterSpacing="0.01em" mb={5}>
        How this compares
      </Text>

      <Flex gap={{ base: 6, md: 10 }} wrap="wrap" mb={6}>
        <Box>
          <Text fontSize="40px" fontWeight={700} color="ink" lineHeight={1} fontVariantNumeric="tabular-nums">
            {overall}
          </Text>
          <Text fontSize="12px" color="inkMuted" mt={1}>
            this store
          </Text>
        </Box>
        {peers.cohort && (
          <Box>
            <Text fontSize="40px" fontWeight={700} color="ink" lineHeight={1} fontVariantNumeric="tabular-nums">
              {peers.cohort.low === peers.cohort.high
                ? peers.cohort.low
                : `${peers.cohort.low}\u2013${peers.cohort.high}`}
            </Text>
            <Text fontSize="12px" color="inkMuted" mt={1}>
              {/* Every store on this platform including this one. Saying
                  "others" would be a false claim in a document we hand to a
                  client, and the point is stronger anyway: they are inside the
                  band, and the band is the platform. */}
              all {peers.cohort.count} on {peers.cohort.platform}
            </Text>
          </Box>
        )}
        <Box>
          <Text fontSize="40px" fontWeight={700} color="accent.500" lineHeight={1} fontVariantNumeric="tabular-nums">
            {peers.best}
          </Text>
          <Text fontSize="12px" color="inkMuted" mt={1}>
            best we have measured
          </Text>
        </Box>
      </Flex>

      <Text fontSize="md" color="inkMuted" lineHeight={1.68} maxW="measure">
        {onTemplate && peers.cohort
          ? `All ${peers.cohort.count} ${peers.cohort.platform} dealerships we have audited, this one included, score inside that narrow band and fail in the same places. That is the platform, not the store.`
          : 'Scored against every dealership we have audited, the same way, on the same checks.'}
        {' '}
        {peers.bestIsCustom && gap > 0
          ? `The highest scoring site we have measured, at ${peers.best}, is not on a dealer platform at all. Somebody built it for them. It is the same industry and the same customers, which is the point: this is ${gap} points of work, not a different business.`
          : gap > 0
            ? `The best we have measured is ${peers.best}.`
            : 'Nothing we have measured scores higher.'}
      </Text>

      <Text fontFamily="mono" fontSize="11px" color="inkMuted" mt={4}>
        Across {peers.audited} dealerships audited to date
      </Text>
    </Box>
  )
}

export default function StorefrontReport() {
  const { token } = useParams()
  const [state, setState] = useState({ loading: true, data: null, error: null })

  useEffect(() => {
    let live = true
    fetch(`/.netlify/functions/storefront-view?token=${encodeURIComponent(token)}`)
      .then((r) => r.json().then((b) => ({ ok: r.ok, b })))
      .then(({ ok, b }) => {
        if (!live) return
        setState(ok ? { loading: false, data: b, error: null } : { loading: false, data: null, error: b.error })
      })
      .catch(() => live && setState({ loading: false, data: null, error: 'Could not load this report.' }))
    return () => {
      live = false
    }
  }, [token])

  if (state.loading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="card">
        <Spinner size="lg" color="ink" thickness="3px" />
      </Flex>
    )
  }

  if (state.error || !state.data) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="card" px={6}>
        <VStack spacing={3} maxW="measure" textAlign="center">
          <Text fontSize="xl" fontWeight={600} color="ink">
            This report is not available
          </Text>
          <Text fontSize="md" color="inkMuted" lineHeight={1.6}>
            The link may have expired or been mistyped. Ask whoever sent it for a fresh one.
          </Text>
        </VStack>
      </Flex>
    )
  }

  const d = state.data
  // A report about one building names the building. A report about the group
  // homepage names the business.
  const name = d.location?.name || d.dealership?.name || d.evidence?.title?.split(/[|\-–]/)[0]?.trim() || 'This dealership'
  const place = [d.location?.city || d.dealership?.city, d.location?.state || d.dealership?.state]
    .filter(Boolean).join(', ')
  const parent = d.location && d.dealership?.name && d.dealership.name !== d.location.name ? d.dealership.name : null
  const scores = d.scores || {}
  const entries = Object.entries(CATEGORY_COPY).filter(([k]) => scores[k] != null)
  const lowest = entries.reduce((a, [k]) => (scores[k] < (scores[a] ?? 101) ? k : a), entries[0]?.[0])
  const fixFirst = (d.findings || []).filter((f) => f.severity === 'high')
  const rest = (d.findings || []).filter((f) => f.severity !== 'high')
  const when = new Date(d.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <Box bg="card" minH="100vh">
      <Box maxW="820px" mx="auto" px={{ base: 5, md: 8 }} py={{ base: 10, md: 14 }}>

        <Flex justify="space-between" align="baseline" gap={4} wrap="wrap" mb={9}>
          <Box
            border="1.5px solid" borderColor="inkMuted" borderRadius="5px" px={3} py={1}
            fontFamily="mono" fontSize="13px" fontWeight={700} letterSpacing="0.06em" color="ink"
          >
            J<Box as="span" color="accent.500" px="2px">|</Box>13
          </Box>
          <Text fontFamily="mono" fontSize="11px" letterSpacing="0.1em" textTransform="uppercase" color="inkMuted">
            Janda Digital &middot; Storefront Report
          </Text>
        </Flex>

        <Box borderBottom="1px solid" borderColor="line" pb={8}>
          {place && (
            <Text fontFamily="mono" fontSize="11px" letterSpacing="0.13em" textTransform="uppercase" color="accent.500" mb={2}>
              {place}
            </Text>
          )}
          {/* Their own mark on a report about them, the way a consultancy puts
              the client's logo on the cover. The rest of the document stays in
              Janda Digital's colours, because it is our assessment and not
              their brochure. */}
          {d.dealership?.logo_path && (
            <Box mb={4}>
              <DealershipLogo dealership={d.dealership} maxH="40px" />
            </Box>
          )}
          <Text fontSize={{ base: '38px', md: '56px' }} fontWeight={700} color="ink" lineHeight={1.05} letterSpacing="-0.02em">
            {name}
          </Text>
          {parent && (
            <Text fontSize="md" color="inkMuted" mt={1}>
              {parent}
            </Text>
          )}
          <Text fontFamily="mono" fontSize="13px" color="inkMuted" mt={2} wordBreak="break-all">
            {d.site_url.replace(/^https?:\/\//, '').replace(/\/$/, '')} &middot; reviewed {when}
          </Text>
        </Box>

        <Flex align="flex-end" gap={4} pt={9} pb={6} borderBottom="2px solid" borderColor="ink">
          <Text fontSize={{ base: '80px', md: '112px' }} fontWeight={700} color="ink" lineHeight={0.82} letterSpacing="-0.03em" fontVariantNumeric="tabular-nums">
            {d.overall ?? '--'}
          </Text>
          <Text fontSize="2xl" color="inkMuted" pb={2}>/100</Text>
          {d.evidence?.platform && (
            <Text ml="auto" fontSize="sm" color="inkMuted" textAlign="right" maxW="30ch" pb={1} lineHeight={1.5}>
              Site built on {d.evidence.platform.name}
            </Text>
          )}
        </Flex>

        <Box>
          {entries.map(([k, [label, blurb]]) => (
            <ScoreRow key={k} name={label} blurb={blurb} value={scores[k]} worst={k === lowest} />
          ))}
        </Box>

        {d.evidence?.psi_reason && (
          <Text fontFamily="mono" fontSize="11.5px" color="inkMuted" mt={5} lineHeight={1.7}>
            Some speed checks could not be run today. They are left out of the calculation rather
            than counted against you.
          </Text>
        )}

        <HowThisCompares peers={d.peers} overall={d.overall} platform={d.evidence?.platform} />

        {(d.connections || []).length > 0 && (
          <Box mt={14}>
            <Text fontFamily="mono" fontSize="11px" letterSpacing="0.13em" textTransform="uppercase" color="inkMuted" mb={3}>
              The argument
            </Text>
            <Text fontSize={{ base: '26px', md: '32px' }} fontWeight={600} color="ink" textTransform="uppercase" letterSpacing="0.01em" mb={6}>
              How it connects
            </Text>
            <VStack align="stretch" spacing={8} bg="surface" borderRadius="3px" p={{ base: 6, md: 9 }}>
              {d.connections.map((c) => (
                <Box key={c.title}>
                  <Text fontSize="xl" fontWeight={600} color="ink" mb={2.5} lineHeight={1.25} maxW="30ch">
                    {c.title}
                  </Text>
                  <Text fontSize="md" color="inkMuted" lineHeight={1.68} maxW="measure">
                    {c.body}
                  </Text>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        {fixFirst.length > 0 && (
          <Box mt={14}>
            <Text fontFamily="mono" fontSize="11px" letterSpacing="0.13em" textTransform="uppercase" color="inkMuted" mb={3}>
              Ranked by what it costs you
            </Text>
            <Text fontSize={{ base: '26px', md: '32px' }} fontWeight={600} color="ink" textTransform="uppercase" letterSpacing="0.01em" mb={4}>
              Fix first
            </Text>
            {fixFirst.map((f, i) => (
              <Flex key={f.title} gap={5} py={5} borderBottom="1px solid" borderColor="line" align="baseline">
                <Text fontSize="34px" fontWeight={700} color="inkMuted" lineHeight={1} minW="34px" fontVariantNumeric="tabular-nums">
                  {i + 1}
                </Text>
                <Box>
                  <Text fontSize="xl" fontWeight={600} color="ink" mb={1.5} lineHeight={1.25}>
                    {f.title}
                  </Text>
                  <Text fontSize="md" color="inkMuted" lineHeight={1.65} maxW="measure">
                    {f.detail}
                  </Text>
                </Box>
              </Flex>
            ))}
          </Box>
        )}

        {rest.length > 0 && (
          <Box mt={14}>
            <Text fontFamily="mono" fontSize="11px" letterSpacing="0.13em" textTransform="uppercase" color="inkMuted" mb={3}>
              Also worth knowing
            </Text>
            <Text fontSize={{ base: '26px', md: '32px' }} fontWeight={600} color="ink" textTransform="uppercase" letterSpacing="0.01em" mb={2}>
              Smaller findings
            </Text>
            {rest.map((f) => <Finding key={f.title} f={f} />)}
          </Box>
        )}

        <Box mt={16} pt={8} borderTop="2px solid" borderColor="ink">
          <Text fontFamily="mono" fontSize="11px" letterSpacing="0.13em" textTransform="uppercase" color="accent.500" mb={3}>
            Janda Digital
          </Text>
          <Text fontSize={{ base: '26px', md: '32px' }} fontWeight={600} color="ink" textTransform="uppercase" letterSpacing="0.01em" mb={4}>
            We can do this work
          </Text>
          <Text fontSize="md" color="inkMuted" lineHeight={1.68} maxW="measure" mb={3}>
            Everything in this report is work we do. The items under Fix First are a short
            engagement, not a project. If you would rather keep your marketing, your website and
            your training in one place instead of across vendors who do not talk to each other,
            that is what Janda Digital is for.
          </Text>
          <Text fontSize="md" color="inkMuted" lineHeight={1.68} maxW="measure">
            We re-run this report monthly, so the next one shows what moved.
          </Text>
        </Box>

        <Divider borderColor="line" mt={12} mb={5} />
        <Flex justify="space-between" gap={3} wrap="wrap">
          <Text fontFamily="mono" fontSize="11px" letterSpacing="0.09em" textTransform="uppercase" color="inkMuted">
            Janda Digital &middot; A division of Janda Dealer Training
          </Text>
          <Text fontFamily="mono" fontSize="11px" letterSpacing="0.09em" textTransform="uppercase" color="inkMuted">
            jandatraining.com
          </Text>
        </Flex>
      </Box>
    </Box>
  )
}
